import React, { createContext, useMemo } from 'react';
import { Permissions, TreeFile, TreeFolder } from '../services';
// Actions that can be performed on a folder or file
type NodeKinds = 'folder' | 'file';

interface SelectedNode {
  kind: NodeKinds;
  path: string | undefined | null;
  original: TreeFile | TreeFolder;
}

export interface PathNode {
  id: number;
  name: string;
}

export interface ActiveFolder {
  id: number;
  name: string;
  path: PathNode[];
  content: {
    files: TreeFile[];
    folders: TreeFolder[];
  };
  permissions: Permissions
  // UI related states
  isLoading?: boolean;
  isFetching?: boolean;
  shareable_link: string;
}

interface FileExplorerContextTypes {
  selectedNode: SelectedNode | undefined | null;
  setSelectedNode: React.Dispatch<React.SetStateAction<SelectedNode | null>>;
  activeFolder: ActiveFolder | undefined | null;
  setActiveFolder: React.Dispatch<React.SetStateAction<ActiveFolder | null>>;
  newlyAddedItems: Set<number>;
  addNewlyAddedItems: (itemIds: number[]) => void;
  removeNewlyAddedItem: (itemId: number) => void;
  userIsSuperuser: boolean;
}

// Initialize with proper default values that satisfy the interface
export const FileExplorerContext = createContext<FileExplorerContextTypes>({
  selectedNode: undefined,
  setSelectedNode: () => null,
  activeFolder: undefined,
  setActiveFolder: () => null,
  newlyAddedItems: new Set(),
  addNewlyAddedItems: () => {},
  removeNewlyAddedItem: () => {},
  userIsSuperuser: false,
});

/**
 * FileExplorerContextProvider manages the state of the currently selected file or folder
 * in the file explorer. It provides this selection state and a setter function to all
 * child components, allowing them to read or update the selected node throughout the
 * file explorer component tree without prop drilling.
 */
export function FileExplorerContextProvider(
  { children, userIsSuperuser }: { children: React.ReactNode, userIsSuperuser: boolean }) {
  const [selectedNode, setSelectedNode] = React.useState<SelectedNode | null>(null);
  const [activeFolder, setActiveFolder] = React.useState<ActiveFolder | null>(null);
  const [newlyAddedItems, setNewlyAddedItems] = React.useState<Set<number>>(new Set());

  const removeNewlyAddedItem = React.useCallback((itemId: number) => {
    setNewlyAddedItems((prev) => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });
  }, []);
  const addNewlyAddedItems = React.useCallback((itemIds: number[]) => {
    setNewlyAddedItems((prev) => new Set([...prev, ...itemIds]));
    itemIds.forEach((itemId) => {
      setTimeout(() => {
        removeNewlyAddedItem(itemId);
      }, 3000);
    });
  }, []);

  const values = useMemo(() => ({
    selectedNode,
    setSelectedNode,
    activeFolder,
    setActiveFolder,
    userIsSuperuser,
    newlyAddedItems,
    addNewlyAddedItems,
    removeNewlyAddedItem,
  }), [selectedNode, activeFolder, newlyAddedItems, addNewlyAddedItems, removeNewlyAddedItem]);

  return (
    <FileExplorerContext.Provider value={values}>
      {children}
    </FileExplorerContext.Provider>
  );
}
